import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PrinterCheck, Plus, DollarSign, Trash2 } from "lucide-react";
import EditableGrid, { type GridColumn } from "@/components/ui/editable-grid";
import AddClassDialog from "@/components/dialogs/add-class-dialog";
import PageHeader from "@/components/layout/page-header";
import { useDialogs } from "@/contexts/dialog-context";
import { getCurrentGradeString, getCurrentGradeCode } from "@/lib/gradeUtils";
import { formatPhoneNumber } from "@/lib/phoneUtils";
import type { Class, ClassFee, Grade, InsertClass } from "@shared/schema";

const MAX_FEES_PER_CLASS = 7;

interface FeeRow {
  id?: number;
  description: string;
  amount: string;
}

export default function Classes() {
  const { toast } = useToast();
  const { addClassOpen, setAddClassOpen } = useDialogs();
  const [feeDialogOpen, setFeeDialogOpen] = useState(false);
  const [feeDialogClass, setFeeDialogClass] = useState<Class | null>(null);
  const [feeRows, setFeeRows] = useState<FeeRow[]>([]);
  const [deletedFeeIds, setDeletedFeeIds] = useState<number[]>([]);
  const [savingFees, setSavingFees] = useState(false);

  const { data: classes, isLoading: classesLoading } = useQuery({
    queryKey: ["/api/classes"],
    queryFn: async () => {
      const response = await fetch("/api/classes", { credentials: "include" });
      if (!response.ok) throw new Error(`${response.status}: ${response.statusText}`);
      return response.json();
    },
    retry: false,
  });

  const { data: grades } = useQuery({
    queryKey: ["/api/grades"],
    queryFn: async () => {
      const response = await fetch("/api/grades", { credentials: "include" });
      if (!response.ok) throw new Error(`${response.status}: ${response.statusText}`);
      return response.json();
    },
    retry: false,
  });

  const { data: classFees } = useQuery({
    queryKey: ["/api/class-fees"],
    queryFn: async () => {
      const response = await fetch("/api/class-fees", { credentials: "include" });
      if (!response.ok) throw new Error(`${response.status}: ${response.statusText}`);
      return response.json();
    },
    retry: false,
  });

  const { data: students } = useQuery({
    queryKey: ["/api/students"],
    queryFn: async () => {
      const response = await fetch("/api/students", { credentials: "include" });
      if (!response.ok) throw new Error(`${response.status}: ${response.statusText}`);
      return response.json();
    },
    retry: false,
  });

  const { data: settings } = useQuery({
    queryKey: ["/api/settings"],
    queryFn: async () => {
      const response = await fetch("/api/settings", { credentials: "include" });
      if (!response.ok) throw new Error(`${response.status}: ${response.statusText}`);
      return response.json();
    },
    retry: false,
  });

  const updateClassMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: number; updates: Record<string, any> }) => {
      return await apiRequest(`/api/classes/${id}`, "PATCH", updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/classes"] });
      toast({
        title: "Class Updated",
        description: "Class has been successfully updated.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteClassMutation = useMutation({
    mutationFn: async (classId: number) => {
      await apiRequest(`/api/classes/${classId}`, "DELETE");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/classes"] });
      toast({
        title: "Class Deleted",
        description: "Class has been successfully deleted.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleUpdateClass = async (id: number, updates: Record<string, any>) => {
    const processedUpdates = { ...updates };
    
    // Convert grade codes to integers
    if (updates.startCode !== undefined) {
      processedUpdates.startCode = parseInt(updates.startCode);
    }
    if (updates.endCode !== undefined) {
      processedUpdates.endCode = parseInt(updates.endCode);
    }
    
    await updateClassMutation.mutateAsync({ id, updates: processedUpdates });
  };

  const handleDeleteClass = async (classId: number) => {
    if (confirm("Are you sure you want to delete this class?")) {
      await deleteClassMutation.mutateAsync(classId);
    }
  };

  // Create grade options for dropdown
  const gradeOptions = Array.isArray(grades) ? grades.map((grade: Grade) => ({
    value: grade.code,
    label: grade.gradeName
  })) : [];

  const getFeesForClass = (classId: number): ClassFee[] => {
    return (Array.isArray(classFees) ? classFees : []).filter((fee: ClassFee) => fee.classId === classId);
  };

  const handleEditFees = (classData: Class) => {
    setFeeDialogClass(classData);
    setFeeRows(getFeesForClass(classData.id).map((fee: ClassFee) => ({
      id: fee.id,
      description: fee.description,
      amount: parseFloat(fee.amount).toFixed(2),
    })));
    setDeletedFeeIds([]);
    setFeeDialogOpen(true);
  };

  const updateFeeRow = (index: number, field: 'description' | 'amount', value: string) => {
    setFeeRows(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const removeFeeRow = (index: number) => {
    const row = feeRows[index];
    if (row.id) {
      setDeletedFeeIds(prev => [...prev, row.id!]);
    }
    setFeeRows(prev => prev.filter((_, i) => i !== index));
  };

  const addFeeRow = () => {
    setFeeRows(prev => [...prev, { description: "", amount: "" }]);
  };

  const handleSaveFees = async () => {
    if (!feeDialogClass) return;

    // Validate all rows before saving anything
    for (const row of feeRows) {
      if (!row.description.trim()) {
        toast({
          title: "Missing Description",
          description: "Every fee needs a description.",
          variant: "destructive",
        });
        return;
      }
      const amount = parseFloat(row.amount);
      if (isNaN(amount) || amount < 0) {
        toast({
          title: "Invalid Amount",
          description: `"${row.description.trim()}" needs a valid dollar amount.`,
          variant: "destructive",
        });
        return;
      }
    }

    setSavingFees(true);
    try {
      for (const feeId of deletedFeeIds) {
        await apiRequest(`/api/class-fees/${feeId}`, "DELETE");
      }

      for (const row of feeRows) {
        const description = row.description.trim();
        const amount = parseFloat(row.amount).toFixed(2);

        if (row.id) {
          const original = getFeesForClass(feeDialogClass.id).find((fee: ClassFee) => fee.id === row.id);
          if (original && (original.description !== description || parseFloat(original.amount).toFixed(2) !== amount)) {
            await apiRequest(`/api/class-fees/${row.id}`, "PATCH", { description, amount });
          }
        } else {
          await apiRequest(`/api/class-fees`, "POST", {
            classId: feeDialogClass.id,
            description,
            amount,
          });
        }
      }

      queryClient.invalidateQueries({ queryKey: ["/api/class-fees"] });
      toast({
        title: "Fees Updated",
        description: `Fees for ${feeDialogClass.className} have been saved.`,
      });
      setFeeDialogOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save class fees",
        variant: "destructive",
      });
    } finally {
      setSavingFees(false);
    }
  };



  const handlePrintClassRosters = () => {
    if (!classes || !students || !settings || !grades) return;
    
    // Generate roster HTML for elementary classes
    const rosterHTML = generateClassRostersHTML();
    
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(rosterHTML);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const generateClassRostersHTML = () => {
    // Include all classes regardless of grade level
    const allClasses = (classes || []);
    
    // Sort classes by name
    const sortedClasses = allClasses.sort((a: any, b: any) => 
      Number(a.startCode) < Number(b.startCode)
    );
    
    const classRosters = sortedClasses.map((cls: any, index: number) => 
      generateSingleClassRosterHTML(cls, index > 0)
    ).join('');

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Class Rosters</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              color: #333;
            }
            .page-break { 
              page-break-before: always; 
            }
            .class-header {
              font-size: 18px;
              font-weight: bold;
              margin-bottom: 20px;
              border-bottom: 2px solid #000;
              padding-bottom: 10px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 40px;
            }
            th, td {
              border: 1px solid #000;
              padding: 4px 12px;
              text-align: left;
              white-space: nowrap;
              height: 28px;
              vertical-align: middle;
            }
            th {
              background-color: #f5f5f5;
              font-weight: bold;
            }
            .number-column { width: 5%; text-align: center; }
            .grade-column { width: 8%; text-align: center; }
            .name-column { width: 15%; }
            .birth-column { width: 12%; text-align: center; }
            .parent-column { width: 15%; }
            .phone-column { width: 15%; white-space: nowrap; }
            .email-column { width: 30%; }
          </style>
        </head>
        <body>
          ${classRosters}
        </body>
      </html>
    `;
  };

  const generateSingleClassRosterHTML = (classData: any, addPageBreak: boolean = false) => {
    // Get students in this class (based on grade codes)
    const classStudents = (students || []).filter((student: any) => {
      const gradeCode = getCurrentGradeCode(student.gradYear, settings);
      return gradeCode !== null && gradeCode >= classData.startCode && gradeCode <= classData.endCode;
    });

    // Sort students by last name, then first name
    const sortedStudents = classStudents.sort((a: any, b: any) => {
      const lastNameCompare = a.lastName.localeCompare(b.lastName);
      if (lastNameCompare !== 0) return lastNameCompare;
      return a.firstName.localeCompare(b.firstName);
    });

    const rowsHTML = sortedStudents.map((student: any, index: number) => {
      const birthDate = student.birthdate ? new Date(student.birthdate).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '';
      const gradeName = getCurrentGradeString(student.gradYear, settings, grades || []);
      const formattedPhone = formatPhoneNumber(student.family?.parentCell || '');
      
      return `
        <tr>
          <td class="number-column">${index + 1}.</td>
          <td class="grade-column">${gradeName}</td>
          <td class="name-column">${student.lastName}</td>
          <td class="name-column">${student.firstName}</td>
          <td class="birth-column">${birthDate}</td>
          <td class="parent-column">${student.family?.mother || ''}</td>
          <td class="parent-column">${student.family?.father || ''}</td>
          <td class="phone-column">${formattedPhone}</td>
          <td class="email-column">${student.family?.email || ''}</td>
        </tr>
      `;
    }).join('');

    return `
      ${addPageBreak ? '<div class="page-break"></div>' : ''}
      <div class="class-header">${classData.className} - Class Roster</div>
      <table>
        <thead>
          <tr>
            <th class="number-column"></th>
            <th class="grade-column">Grade</th>
            <th class="name-column">Last Name</th>
            <th class="name-column">First Name</th>
            <th class="birth-column">Birth Date</th>
            <th class="parent-column">Mother</th>
            <th class="parent-column">Father</th>
            <th class="phone-column">Parent's Cell</th>
            <th class="email-column">Email</th>
          </tr>
        </thead>
        <tbody>
          ${rowsHTML}
        </tbody>
      </table>
    `;
  };

  const columns: GridColumn[] = [
    { key: "className", label: "Class Name", sortable: true, editable: true, width: "40" },
    { key: "startCode", label: "Start Grade", sortable: true, editable: false, width: "20", type: "dropdown", options: gradeOptions },
    { key: "endCode", label: "End Grade", sortable: true, editable: false, width: "20", type: "dropdown", options: gradeOptions },
    { key: "feesSummary", label: "Fees", sortable: false, editable: false, width: "60" },
  ];

  // Add a read-only fees summary to each class row
  const classesWithFees = (Array.isArray(classes) ? classes : []).map((classData: Class) => ({
    ...classData,
    feesSummary: getFeesForClass(classData.id)
      .map((fee: ClassFee) => `${fee.description} $${parseFloat(fee.amount).toFixed(2)}`)
      .join(" · "),
  }));

  return (
    <div className="h-full flex flex-col">
      <PageHeader 
        title="Classes"
        description="Manage grade-based class groupings"
        actionButton={{
          label: "Add Class",
          onClick: () => setAddClassOpen(true),
          icon: Plus
        }}
        secondaryButton={{
          label: "Print Class Rosters",
          onClick: handlePrintClassRosters,
          variant: "outline",
          icon: PrinterCheck
        }}
      />
      <div className="flex-1 p-6 overflow-hidden">
        <EditableGrid
          data={classesWithFees}
          columns={columns}
          onRowUpdate={handleUpdateClass}
          onRowDelete={handleDeleteClass}
          isLoading={classesLoading}
          customRowAction={{
            label: "Edit Fees",
            icon: DollarSign,
            onClick: handleEditFees
          }}
        />

        <AddClassDialog
          open={addClassOpen}
          onOpenChange={setAddClassOpen}
        />

        {/* Class Fees Edit Dialog */}
        <Dialog open={feeDialogOpen} onOpenChange={setFeeDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Edit Fees - {feeDialogClass?.className}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {feeRows.length === 0 ? (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  No fees for this class yet. Click "Add Fee" to create one.
                </p>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-[1fr_130px_40px] gap-3 items-center">
                    <Label className="text-sm font-medium">Description</Label>
                    <Label className="text-sm font-medium">Amount</Label>
                    <span />
                  </div>
                  {feeRows.map((row, index) => (
                    <div key={row.id ?? `new-${index}`} className="grid grid-cols-[1fr_130px_40px] gap-3 items-center">
                      <Input
                        value={row.description}
                        placeholder="e.g. Notebook Fee"
                        onChange={(e) => updateFeeRow(index, 'description', e.target.value)}
                      />
                      <Input
                        value={row.amount}
                        placeholder="0.00"
                        inputMode="decimal"
                        onChange={(e) => updateFeeRow(index, 'amount', e.target.value)}
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                        title="Remove fee"
                        onClick={() => removeFeeRow(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={addFeeRow}
                disabled={feeRows.length >= MAX_FEES_PER_CLASS}
              >
                <Plus className="mr-2 h-4 w-4" />
                {feeRows.length >= MAX_FEES_PER_CLASS ? `Maximum ${MAX_FEES_PER_CLASS} fees` : "Add Fee"}
              </Button>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setFeeDialogOpen(false)} disabled={savingFees}>
                  Cancel
                </Button>
                <Button onClick={handleSaveFees} disabled={savingFees}>
                  {savingFees ? "Saving..." : "Save Fees"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
